import { useEffect, useState } from 'react';
import api from '../../services/api';
import EmployerLayout from '../../layouts/EmployerLayout';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import toast from 'react-hot-toast';

function InfoRow({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-sm font-medium text-gray-800">{value || '—'}</p>
    </div>
  );
}

export default function EmployerWorkerSearch() {
  const [workers, setWorkers] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    availability: '', verification_status: '', min_exp: '', max_wage: '',
  });

  const [viewWorker, setViewWorker]   = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  function setFilter(key) {
    return (e) => { setFilters((p) => ({ ...p, [key]: e.target.value })); setPage(1); };
  }

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 12 });
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    api.get(`/employer/workers/search?${params}`)
      .then((r) => { setWorkers(r.data.data || []); setPagination(r.data.pagination || {}); })
      .catch((err) => {
        if (err?.response?.status === 403) {
          toast.error('Your profile must be verified by admin before searching workers.');
        }
      })
      .finally(() => setLoading(false));
  }, [page, filters]);

  async function saveWorker(workerId) {
    setSaving(workerId);
    try {
      await api.post('/employer/workers/save', { workerId });
      toast.success('Worker saved');
    } catch {}
    finally { setSaving(null); }
  }

  async function openWorkerDetail(workerId) {
    setViewLoading(true);
    setViewWorker({ id: workerId, _loading: true });
    try {
      const res = await api.get(`/employer/workers/${workerId}`);
      setViewWorker(res.data.data);
    } catch {
      toast.error('Could not load worker details');
      setViewWorker(null);
    } finally {
      setViewLoading(false);
    }
  }

  return (
    <EmployerLayout>
      <div className="space-y-5">
        <h1 className="text-xl font-bold text-gray-900">Search Workers</h1>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Availability</label>
            <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none"
              value={filters.availability} onChange={setFilter('availability')}>
              <option value="">Any</option>
              <option value="available">Available</option>
              <option value="on_job">On Job</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Verification</label>
            <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none"
              value={filters.verification_status} onChange={setFilter('verification_status')}>
              <option value="">Any</option>
              <option value="verified">Verified only</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Min Experience (yrs)</label>
            <input type="number" min="0" value={filters.min_exp} onChange={setFilter('min_exp')}
              placeholder="0"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none" />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Max Daily Wage (₹)</label>
            <input type="number" min="0" value={filters.max_wage} onChange={setFilter('max_wage')}
              placeholder="Any"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:outline-none" />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin h-8 w-8 border-4 border-india border-t-transparent rounded-full" />
          </div>
        ) : workers.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <span className="text-4xl">🔍</span>
            <p className="text-gray-500 mt-3">No workers found</p>
            <p className="text-sm text-gray-400 mt-1">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {workers.map((w) => (
              <div key={w.id} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-start gap-3">
                  {w.profile_photo_url ? (
                    <img src={w.profile_photo_url} alt={w.full_name}
                      className="w-11 h-11 rounded-full object-cover flex-shrink-0 border border-gray-200" />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center font-bold text-gray-500 flex-shrink-0 text-lg">
                      {w.full_name?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold text-gray-900 text-sm">{w.full_name}</h3>
                      <Badge label={w.verification_status} variant={w.verification_status} />
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-xs text-gray-500">
                      {w.state && <span>🗺 {w.state}</span>}
                      {w.district && <span>📍 {w.district}</span>}
                      {w.experience_years > 0 && <span>⏳ {w.experience_years} yrs exp</span>}
                      {w.daily_wage_min && <span>💰 ₹{w.daily_wage_min}/day min</span>}
                    </div>
                    <div className="mt-1.5">
                      <Badge label={w.availability_status} variant={w.availability_status} />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="secondary" className="flex-1"
                    onClick={() => openWorkerDetail(w.id)}>
                    View Profile
                  </Button>
                  <Button size="sm" className="flex-1"
                    loading={saving === w.id} onClick={() => saveWorker(w.id)}>
                    Save Worker
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {pagination.totalPages > 1 && (
          <div className="flex justify-center gap-3">
            {pagination.hasPrev && (
              <Button size="sm" variant="secondary" onClick={() => setPage((p) => p - 1)}>← Prev</Button>
            )}
            <span className="text-sm text-gray-500 py-1.5">Page {pagination.page} of {pagination.totalPages}</span>
            {pagination.hasNext && (
              <Button size="sm" variant="secondary" onClick={() => setPage((p) => p + 1)}>Next →</Button>
            )}
          </div>
        )}
      </div>

      {/* ── Worker Detail Modal ── */}
      {viewWorker && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-lg my-6">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h3 className="font-bold text-gray-900">Worker Profile</h3>
              <button onClick={() => setViewWorker(null)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>

            {viewLoading && viewWorker._loading ? (
              <div className="p-12 text-center text-gray-400">
                <div className="animate-spin h-8 w-8 border-4 border-brand-500 border-t-transparent rounded-full mx-auto mb-3" />
                Loading worker details...
              </div>
            ) : (
              <div className="overflow-y-auto max-h-[80vh]">
                {/* Identity banner */}
                <div className="bg-gradient-to-r from-brand-800 to-brand-600 p-5 flex items-center gap-4">
                  {viewWorker.profile_photo_url ? (
                    <img src={viewWorker.profile_photo_url} alt={viewWorker.full_name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-white/30 flex-shrink-0" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                      {viewWorker.full_name?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                  <div className="text-white">
                    <h2 className="font-bold text-lg leading-tight">{viewWorker.full_name}</h2>
                    <p className="text-brand-200 text-xs mt-0.5 font-mono">{viewWorker.registration_number}</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <Badge label={viewWorker.verification_status} variant={viewWorker.verification_status} />
                      <Badge label={viewWorker.availability_status} variant={viewWorker.availability_status} />
                    </div>
                  </div>
                </div>

                <div className="p-5 space-y-5">
                  {/* Personal */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Personal</p>
                    <div className="grid grid-cols-2 gap-4">
                      <InfoRow label="Age" value={viewWorker.age ? `${viewWorker.age} years` : null} />
                      <InfoRow label="Gender" value={viewWorker.gender ? viewWorker.gender.charAt(0).toUpperCase() + viewWorker.gender.slice(1) : null} />
                      <InfoRow label="Guardian / Father" value={viewWorker.guardian_name} />
                      <InfoRow label="Education" value={viewWorker.education_level} />
                    </div>
                  </div>

                  {/* Location */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Location</p>
                    <div className="grid grid-cols-2 gap-4">
                      <InfoRow label="State" value={viewWorker.state} />
                      <InfoRow label="District" value={viewWorker.district} />
                      <InfoRow label="Village / Town" value={viewWorker.city_village} />
                      <InfoRow label="Pincode" value={viewWorker.pincode} />
                    </div>
                    {viewWorker.address_line && (
                      <div className="mt-3">
                        <p className="text-xs text-gray-400">Address</p>
                        <p className="text-sm font-medium text-gray-800">{viewWorker.address_line}</p>
                      </div>
                    )}
                  </div>

                  {/* Work Details */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Work Details</p>
                    <div className="grid grid-cols-2 gap-4">
                      <InfoRow label="Experience" value={viewWorker.experience_years != null ? `${viewWorker.experience_years} years` : null} />
                      <InfoRow label="Travel Radius" value={viewWorker.travel_radius_km != null ? `${viewWorker.travel_radius_km} km` : null} />
                      <InfoRow label="Daily Wage (min)" value={viewWorker.daily_wage_min ? `₹${viewWorker.daily_wage_min}` : null} />
                      <InfoRow label="Daily Wage (max)" value={viewWorker.daily_wage_max ? `₹${viewWorker.daily_wage_max}` : null} />
                      {viewWorker.upi_id && <InfoRow label="UPI ID" value={viewWorker.upi_id} />}
                      {viewWorker.mobile_alt && <InfoRow label="Alt Mobile" value={viewWorker.mobile_alt} />}
                    </div>
                  </div>

                  {/* Skills */}
                  {viewWorker.skills?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Skills</p>
                      <div className="flex flex-wrap gap-2">
                        {viewWorker.skills.map((s, i) => (
                          <span key={i} className="px-2.5 py-1 bg-brand-50 text-brand-700 border border-brand-200 rounded-full text-xs font-medium">
                            {s.skill_name}
                            {s.proficiency && <span className="text-brand-400 ml-1">· {s.proficiency}</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Languages */}
                  {viewWorker.languages?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Languages</p>
                      <div className="flex flex-wrap gap-2">
                        {viewWorker.languages.map((l, i) => (
                          <span key={i} className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                            {l.language_name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {viewWorker.expiry_date && (
                    <p className="text-xs text-gray-400 border-t border-gray-100 pt-3">
                      Profile valid until:{' '}
                      <span className="font-medium text-gray-700">
                        {new Date(viewWorker.expiry_date).toLocaleDateString('en-IN')}
                      </span>
                    </p>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 pt-2 border-t border-gray-100">
                    <Button fullWidth onClick={() => { saveWorker(viewWorker.id); setViewWorker(null); }}
                      loading={saving === viewWorker.id}>
                      Save Worker
                    </Button>
                    <Button fullWidth variant="secondary" onClick={() => setViewWorker(null)}>Close</Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </EmployerLayout>
  );
}
